import {
  Drawer,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  Button,
  Heading,
  Text,
  Flex,
  Spacer,
} from '@chakra-ui/react';
import React, { useCallback, useMemo, useState } from 'react';

import { finleyAssert } from '@finley-technologies/common';
import { FinleyAi as FinleyAiTypes } from '@finley-technologies/finley-types';

import { useUserAuthOrThrow } from '../../../contexts/UserAuthProvider';
import { useFinleyAiThreads } from '../hooks/useFinleyAiThreads';
import { FinleyAiAthenaSqlGenerationDrawerChatView } from './FinleyAiAthenaSqlGenerationDrawerChatView';
import { FinleyAiAthenaSqlGenerationDrawerTablesView } from './FinleyAiAthenaSqlGenerationDrawerTablesView';
import { OnClickSqlAction } from './FinleyAiThreadComponents';
import { initFinleyAiViewContext } from './FinleyAiUtils';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { FinleyAiAthenaSqlGenerationDrawerHistoryView } from 'packages/finleyAi/ui/FinleyAiAthenaSqlGenerationDrawerHistoryView';

export type FinleyAiDrawerDisplayMode = 'tables' | 'chat' | 'history';
const drawerDisplayModeToSubheading: Record<
  FinleyAiDrawerDisplayMode,
  string
> = {
  tables: `Finley AI can help you generate SQL queries without writing a single line of code. Specify your requirements and tell us what you're looking for. Start by selecting the tables you'd like to query.`,
  chat: `Finley AI can help you generate SQL queries without writing a single line of code. Specify your requirements and tell us what you're looking for.`,
  history: `Finley AI can help you generate SQL queries without writing a single line of code. Specify your requirements and tell us what you're looking for.`,
};
export const drawerDisplayModeToHeaderNavigationLabel: Record<
  FinleyAiDrawerDisplayMode,
  string
> = {
  tables: 'View History',
  chat: 'View History',
  history: 'New Chat',
};

type FinleyAiAthenaSqlGenerationDrawerContainerProps = {
  isOpen: boolean;
  onClose: () => void;
  creditFacilityId: string;
  onClickSqlAction: OnClickSqlAction;
  organizationId?: string;
};
export const FinleyAiAthenaSqlGenerationDrawerContainer = ({
  isOpen,
  onClose,
  creditFacilityId,
  onClickSqlAction,
  organizationId: organizationIdProp,
}: FinleyAiAthenaSqlGenerationDrawerContainerProps) => {
  const { user } = useUserAuthOrThrow();

  const initialViewContext = initFinleyAiViewContext({
    type: 'athenaSqlGeneration',
    targetOrganizationId: organizationIdProp ?? user.organizationId,
  });

  finleyAssert(
    initialViewContext.type === 'athenaSqlGeneration',
    'Only able to initialize Finley AI for portfolio covenants for SQL generation',
  );

  // State Management
  const [
    drawerDisplayMode,
    setDrawerDisplayMode,
  ] = useState<FinleyAiDrawerDisplayMode>('tables');
  const [tempPrompt, setTempPrompt] = useState<string>('');
  const [prompt, setPrompt] = useState<string>('');

  const {
    onReset,
    selectedThread,
    isThreadLoading,
    viewContext,
    setViewContext,
    isThreadFailed,
    onSendPrompt,
    handleAthenaSqlExplain,
    isProducingMessage,
    handleFinleyAiThreadSelection,
  } = useFinleyAiThreads({
    prompt,
    setPrompt,
    tempPrompt,
    setTempPrompt,
    initialViewContext,
  });

  finleyAssert(
    viewContext.type === 'athenaSqlGeneration',
    'Sql Generation Finley AI can only be used for athenaSqlGeneration type',
  );

  const onCloseDrawer: () => void = useCallback(() => {
    onReset({
      finleyAiType: initialViewContext.type,
      targetOrgId: initialViewContext.targetOrganizationId,
    });
    setDrawerDisplayMode('tables');
    onClose();
  }, [onReset, setDrawerDisplayMode, onClose, initialViewContext]);

  const drawerDisplayModeToHeaderNavigationAction: Record<
    FinleyAiDrawerDisplayMode,
    () => void
  > = useMemo(() => {
    const onViewHistory: () => void = () => {
      onReset({
        finleyAiType: initialViewContext.type,
        targetOrgId: initialViewContext.targetOrganizationId,
      });
      setDrawerDisplayMode('history');
    };

    const onNewChat: () => void = () => {
      onReset({
        finleyAiType: initialViewContext.type,
        targetOrgId: initialViewContext.targetOrganizationId,
      });
      setDrawerDisplayMode('tables');
    };

    return {
      tables: onViewHistory,
      chat: onViewHistory,
      history: onNewChat,
    };
  }, [onReset, setDrawerDisplayMode, initialViewContext]);

  const onSelectHistoricalThread = useCallback(
    (thread: FinleyAiTypes.FinleyAiThread) => {
      handleFinleyAiThreadSelection(thread);
      setDrawerDisplayMode('chat');
    },
    [handleFinleyAiThreadSelection],
  );

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onCloseDrawer} size="xl">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerHeader>
          <Flex alignItems="center">
            <Heading as="h2" fontSize="24px" lineHeight="32px" fontWeight={600}>
              SQL AI
            </Heading>
            <Spacer />

            <Button
              variant="ghost"
              color={colors.black[70]}
              fontWeight={400}
              onClick={
                drawerDisplayModeToHeaderNavigationAction[drawerDisplayMode]
              }
            >
              {drawerDisplayModeToHeaderNavigationLabel[drawerDisplayMode]}
            </Button>

            <Button w="40px" variant="ghost" onClick={onCloseDrawer}>
              <Icon icon="x" />
            </Button>
          </Flex>
          <Text
            mt="24px"
            fontSize="16px"
            fontWeight={400}
            color={colors.black[70]}
          >
            {drawerDisplayModeToSubheading[drawerDisplayMode]}
          </Text>
          {drawerDisplayMode === 'chat' && (
            <Flex alignItems="center" fontSize="16px" fontWeight={400} mt="8px">
              <Text color={colors.black[70]}>
                To change the data source,&nbsp;
              </Text>
              <Text
                cursor="pointer"
                color={colors.info}
                _hover={{ textDecoration: 'underline' }}
                onClick={() => {
                  onReset({
                    finleyAiType: initialViewContext.type,
                    targetOrgId: initialViewContext.targetOrganizationId,
                  });
                  setDrawerDisplayMode('tables');
                }}
              >
                start over.
              </Text>
            </Flex>
          )}
        </DrawerHeader>

        {drawerDisplayMode === 'tables' && (
          <FinleyAiAthenaSqlGenerationDrawerTablesView
            onClickNext={() => setDrawerDisplayMode('chat')}
            viewContext={viewContext}
            setViewContext={setViewContext}
            creditFacilityId={creditFacilityId}
          />
        )}

        {drawerDisplayMode === 'chat' && (
          <FinleyAiAthenaSqlGenerationDrawerChatView
            viewContext={viewContext}
            organizationId={viewContext.targetOrganizationId}
            thread={selectedThread}
            isThreadDisabled={isThreadFailed}
            isThreadFailed={isThreadFailed}
            isProducingMessage={isProducingMessage}
            isGeneratingPromptResponse={isThreadLoading}
            handleAthenaSqlExplain={handleAthenaSqlExplain}
            prompt={prompt}
            setPrompt={setPrompt}
            onSendPrompt={onSendPrompt}
            onClickSqlAction={onClickSqlAction}
            tempPrompt={tempPrompt}
          />
        )}

        {drawerDisplayMode === 'history' && (
          <FinleyAiAthenaSqlGenerationDrawerHistoryView
            userId={user.id}
            viewContext={viewContext}
            handleFinleyAiThreadSelection={onSelectHistoricalThread}
          />
        )}
      </DrawerContent>
    </Drawer>
  );
};
